import { defineStore } from 'pinia';
import { User, APIResponse } from '~/api/apiClient.ts';
import { ProductPlugin } from '~/types/index.ts';
import APIClient from '~/api/apiClient.ts';
import CookieManager from '~/utils/cookieManager.ts';
import { SamplePackTypes } from '~/types/enums.ts';
import useGTag from '~/composables/useGTag';

const { 
  trackPreferredCurrency,
  trackLogout
} = useGTag();

interface UserState {
  userData: User | null;
  products: {
    bundle: any[]; // Define these types more specifically if possible
    other: any[];
    samplepack: any[];
    plugin: ProductPlugin[];
  };
  licenseMap: Record<string, any>;
  preferredCurrency: string | null; // 'USD', 'JPY', 'EUR' or null if not yet determined
  userLoginMail: string | null;
}

const preferedCurrencyCookieName = 'preferredCurrency';

export const useUserStore = defineStore('userStore', {
  state: (): UserState => ({
    userData: null,
    products: {
      bundle: [],
      other: [],
      samplepack: [],
      plugin: []
    },
    licenseMap: {},
    preferredCurrency: CookieManager.getInstance().getCookie(preferedCurrencyCookieName) || null,
    userLoginMail: null
  }),

  getters: {
    preferredCurrencySymbol(): string | null {
      const codeToSymbol = {
        'EUR': '€',
        'USD': '$',
        'JPY': '¥'
      };
      return this.preferredCurrency ? codeToSymbol[this.preferredCurrency] : null;
    },
    hasLicenseForProduct(): (productName: string) => boolean {
      console.log("hasLicenseForProduct getter:", this.licenseMap);
      return (product: string) => {
        return this.licenseMap.hasOwnProperty(product) && this.licenseMap[product] != null;
      };
    },    
    userLicenseMap(): any {
        const licenseMap = {};
        console.log("userLicenseMap getter products:", this.products);
        // Map licenses for plugins
        this.products.plugin.forEach(product => {
          licenseMap[product.name] = {
            hasLicense: product.license !== null && product.license !== undefined,
            license_url: product.license ? product.license.url : null
          };
        });

        // Map licenses for sample packs
        this.products.samplepack.forEach(samplepack => {
          const downloadLinks = {};

          if (samplepack.download) {
            downloadLinks[SamplePackTypes.All] = samplepack.download.complete ? samplepack.download.complete.url : null;
            downloadLinks[SamplePackTypes.Aif] = samplepack.download.AIF ? samplepack.download.AIF.url : null;
            downloadLinks[SamplePackTypes.Kemper] = samplepack.download.Kemper ? samplepack.download.Kemper.url : null;
            downloadLinks[SamplePackTypes.SGear] = samplepack.download["S-Gear"] ? samplepack.download["S-Gear"].url : null;
            downloadLinks[SamplePackTypes.Wave] = samplepack.download.WAV ? samplepack.download.WAV.url : null;
            downloadLinks[SamplePackTypes.Fractal] = samplepack.download.Fractal ? samplepack.download.Fractal.url : null;
          }

          licenseMap[samplepack.name] = {
            hasLicense: samplepack.license !== null && samplepack.license !== undefined,
            downloads: downloadLinks
          };
        });
        console.log("userLicenseMap getter:", licenseMap);
        return licenseMap;
      }
    },
  actions: {
    setUserLoginMail(email) {
      this.userLoginMail = email;
    },
    // In the function where you update the user data:
    setUserData(user_data: User) {
      console.log('setUserData called with:', user_data);
      this.userData = user_data;
    },
    setNewUserName(new_name: string) {
      if (this.userData) {
        this.userData.name = new_name;
      }
    },

    async fetchUserProducts() {
      if(!this.userData || !this.userData.is_logged_in) {
        console.info("User data not set. Cannot fetch user products.");
        return;
      }
      const client = APIClient.getInstance();
      const response: APIResponse = await client.getUserProducts();
      console.log("fetchUserProducts:", response)

      if (response.valid) {
        console.log("User products fetched successfully", response.result.plugin);

        if (response.result.bundle) {
          this.products.bundle = response.result.bundle;
        }
        if (response.result.other) {
          this.products.other = response.result.other;
        }
        if (response.result.samplepack) {
          this.products.samplepack = response.result.samplepack;
        }
        if (response.result.plugin) {
          this.products.plugin = response.result.plugin;
        }
        console.log("User products:", this.products);
        this.generateUserLicenseMap();
      } else {
        console.error("Fetching user products failed:", response.error);
      }
    },

    async logout() {
      const client = APIClient.getInstance();
      const response: APIResponse = await client.logoutUser();
      if (response.valid) {
        console.log("Logout successful");
        trackLogout();
        this.userData = null;
      } else {
        console.error("Logout failed:", response.error);
      }
    },

    determinePreferredCurrencyFromBrowser() {

      console.log("Determining preferred currency from browser:");
     // if (this.preferredCurrency) {
       // console.log("Preferred currency already set:", this.preferredCurrency);
      //  return; // If the currency is already set (from the cookie), don't determine it again
      //}

      const cookie = CookieManager.getInstance().getCookie('preferredCurrency');
      if (cookie) {
        this.preferredCurrency = cookie;
        console.log("Preferred currency loaded from cookie:", this.preferredCurrency);
        return; // If the currency is already set (from the cookie), don't determine it again
      }

      const browserLanguage = navigator.language || navigator.languages[0];

      switch (true) {
        case browserLanguage.startsWith('en-US'):
          this.preferredCurrency = 'USD';
          break;
        case browserLanguage.startsWith('ja'):
          this.preferredCurrency = 'JPY';
          break;
        default:
          this.preferredCurrency = 'EUR';
          break;
      }
      console.log("Preferred currency determined from browser:", this.preferredCurrency);
      // Save the determined currency to a cookie
      CookieManager.getInstance().setPreferenceCookie(preferedCurrencyCookieName, this.preferredCurrency);
      },

    setUserPreferredCurrency(currency: string) {
      this.preferredCurrency = currency;
      console.log("Preferred currency set to:", currency);
      trackPreferredCurrency(currency);
      CookieManager.getInstance().setPreferenceCookie(cookieName, currency);
    },

    setCurrencyBySymbol(symbol: string) {
      const symbolToCode = {
        '€': 'EUR',
        '$': 'USD',
        '¥': 'JPY'
      };
      if (symbolToCode[symbol]) {
        this.preferredCurrency = symbolToCode[symbol];
        // Here you can also add any logic you have when updating the currency
        CookieManager.getInstance().setPreferenceCookie(preferedCurrencyCookieName, this.preferredCurrency);
      }
    },

    formatPriceForUser(priceArray: { EUR: number, JPY: number, USD: number }, discount: number = 0, displayWholeNumber: boolean = false): string {
      if (!this.preferredCurrency) {
        //console.log("Preferred currency not set. Returning empty.");
        return "";
      }
    
      let priceInBaseUnit = priceArray[this.preferredCurrency] / 100; // Convert cents to base currency
      
      // Apply discount
      if (discount > 0 && discount < 100) {
        priceInBaseUnit = priceInBaseUnit * (1 - discount / 100);
      } else if (discount >= 100) {
        console.warn("Discount is >= 100%. Setting price to zero.");
        priceInBaseUnit = 0;
      }
    
      if (displayWholeNumber) {
        priceInBaseUnit = Math.round(priceInBaseUnit);
      }
    

      let formattedPrice = "";

      switch (this.preferredCurrency) {
        case 'USD':
          formattedPrice = `$${priceInBaseUnit.toFixed(2)}`;
          break;
        case 'JPY':
          formattedPrice = `¥${priceInBaseUnit.toFixed(0)}`; // Yen usually doesn't have decimal points
          break;
        case 'EUR':
          formattedPrice = `${priceInBaseUnit.toFixed(2)} €`;
          break;
        default:
          console.error(`Unsupported currency: ${this.preferredCurrency}. Returning price without formatting.`);
          return priceInBaseUnit.toString();
      }
    
      // Check if the after comma value is .00, if true, format as whole number
      if (formattedPrice.endsWith(".00")) {
        return formattedPrice.split(".")[0];
      } else {
        return formattedPrice;
      }
    },

    generateUserLicenseMap() {
      const licenseMap = {};
    
      // Map licenses for plugins
      this.products.plugin.forEach(product => {
        licenseMap[product.name] = {
          hasLicense: product.license !== null && product.license !== undefined,
          license_url: product.license?.url || null
        };
      });
    
      // Map licenses for sample packs
      this.products.samplepack.forEach(samplepack => {
        const downloadLinks = {};
    
        Object.entries(samplepack.download).forEach(([key, value]) => {
          switch (key) {
            case "AIF": 
              downloadLinks[SamplePackTypes.Aif] = value.url; 
              break;
            case "complete": 
              downloadLinks[SamplePackTypes.All] = value.url; 
              break;
            // ... handle other cases
          }
        });
    
        licenseMap[samplepack.name] = {
          hasLicense: samplepack.license !== null && samplepack.license !== undefined,
          downloadLinks: downloadLinks
        };
      });
    
        // Clear existing map
        for (const key of Object.keys(this.licenseMap)) {
          delete this.licenseMap[key];
        }

        // Populate it with new values
        for (const key of Object.keys(licenseMap)) {
          this.licenseMap[key] = licenseMap[key];
        }

    }

  }
});
